import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { coachingNotesService } from '@/services/coachingNotesService';

// Shared edit/delete mutations for one subject's coaching log. Both manage
// surfaces (dashboard CoachDialog, profile CoachingLogSection) invalidate the
// SAME ['coaching-conversations', userId] key and toast identically, so the
// pair lives here instead of being duplicated. Rendering (inline editor,
// AlertDialog confirm) stays with each surface — they have different idioms.
export function useManageCoachingNote(userId: string | undefined) {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['coaching-conversations', userId] });

  const update = useMutation({
    mutationFn: ({ noteId, text }: { noteId: string; text: string }) =>
      coachingNotesService.update(userId!, noteId, text),
    onSuccess: () => {
      invalidate();
      toast.success('Note updated');
    },
    onError: () => {
      toast.error('Could not update the note');
    },
  });

  const remove = useMutation({
    mutationFn: (noteId: string) => coachingNotesService.remove(userId!, noteId),
    onSuccess: () => {
      invalidate();
      toast.success('Note deleted');
    },
    onError: () => {
      toast.error('Could not delete the note');
    },
  });

  return { update, remove };
}
